import { useMemo } from "react";
import { format, addDays } from "date-fns";
import { CourtGridData, CourtGridTimeSlot } from "../../../models/CourtGridData";

interface UseWeeklyFilteringProps {
  weeklyGridData: CourtGridData[];
  startDate: Date;
  isMobile: boolean;
}

const getHourBucket = (time: string): string => {
  const [hours] = time.split(":");
  const paddedHours = hours.padStart(2, "0");
  return `${paddedHours}:00`;
};

export default function useWeeklyFiltering({ weeklyGridData, startDate, isMobile }: UseWeeklyFilteringProps) {
  // Uzupełnienie brakujących dni - zawsze mamy pełny zakres
  const completeWeeklyGridData = useMemo(() => {
    const daysCount = isMobile ? 1 : 7;
    const completeDays: CourtGridData[] = [];

    for (let i = 0; i < daysCount; i++) {
      const currentDate = addDays(startDate, i);
      const formattedDate = format(currentDate, "yyyy-MM-dd");

      // Sprawdź czy mamy dane dla tego dnia
      const existingData = weeklyGridData.find((d) => d.date === formattedDate);

      if (existingData) {
        completeDays.push(existingData);
      } else {
        // Dodaj pusty dzień jeśli nie ma danych z API
        completeDays.push({
          date: formattedDate,
          courts: [
            {
              courtName: "",
              imageUrl: "",
              description: "",
              timeSlots: [],
            },
          ],
        });
      }
    }

    return completeDays;
  }, [weeklyGridData, startDate, isMobile]);

  // Grupowanie slotów w kubełki pełnych godzin per dzień
  const hourlyBuckets = useMemo(() => {
    const buckets: Record<string, Record<string, CourtGridTimeSlot[]>> = {};

    completeWeeklyGridData.forEach((dayData) => {
      if (!dayData.courts || dayData.courts.length === 0) {
        return;
      }

      const dateBuckets = (buckets[dayData.date] = buckets[dayData.date] || {});

      dayData.courts[0].timeSlots.forEach((slot) => {
        const bucketKey = getHourBucket(slot.startTime);
        if (!dateBuckets[bucketKey]) {
          dateBuckets[bucketKey] = [];
        }
        dateBuckets[bucketKey].push(slot);
      });
    });

    return buckets;
  }, [completeWeeklyGridData]);

  // Wyodrębnienie wszystkich pełnych godzin, dla których istnieje jakikolwiek slot (w dowolnym dniu)
  const uniqueHours = useMemo(() => {
    const hours = new Set<string>();

    Object.values(hourlyBuckets).forEach((dateBuckets) => {
      Object.keys(dateBuckets).forEach((bucketKey) => {
        hours.add(bucketKey);
      });
    });

    return Array.from(hours).sort();
  }, [hourlyBuckets]);

  // Funkcja do znalezienia slotu dla danej daty i konkretnej godziny startu
  const findSlot = (date: string, startTime: string): CourtGridTimeSlot | undefined => {
    const dayData = completeWeeklyGridData.find((d) => d.date === date);
    if (!dayData || !dayData.courts || dayData.courts.length === 0) return undefined;
    return dayData.courts[0].timeSlots.find((slot) => slot.startTime === startTime);
  };

  const getSlotsForHour = (date: string, hourLabel: string): CourtGridTimeSlot[] => {
    const dateBuckets = hourlyBuckets[date];
    if (!dateBuckets) return [];
    return dateBuckets[hourLabel] || [];
  };

  return {
    uniqueHours,
    findSlot,
    weeklyGridData: completeWeeklyGridData,
    getSlotsForHour,
  };
}

